import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export type DotTone = 'running' | 'done' | 'failed' | 'approval' | 'idle' | 'paused' | 'info';

const TONE_BG: Record<DotTone, string> = {
  running: 'bg-claude animate-pulse-dot',
  done: 'bg-success',
  failed: 'bg-danger',
  approval: 'bg-warn',
  idle: 'bg-faint',
  paused: 'bg-faint/50',
  info: 'bg-accent',
};
const TONE_TEXT: Record<DotTone, string> = {
  running: 'text-claude',
  done: 'text-success',
  failed: 'text-danger',
  approval: 'text-warn',
  idle: 'text-muted',
  paused: 'text-faint',
  info: 'text-accent',
};

/** Maps run/task/agent statuses onto the status-dot vocabulary. */
export function toneForStatus(status: string | null | undefined): DotTone {
  switch (status) {
    case 'running':
    case 'queued':
    case 'waiting_children':
      return 'running';
    case 'completed':
    case 'done':
    case 'approved':
    case 'active':
      return 'done';
    case 'failed':
    case 'cancelled':
    case 'denied':
    case 'expired':
      return 'failed';
    case 'awaiting_approval':
    case 'awaiting_human':
    case 'pending':
      return 'approval';
    case 'scheduled':
      // Paused on purpose and resumes by itself: calm blue, neither "working" nor "needs you".
      return 'info';
    case 'paused':
    case 'archived':
      return 'paused';
    default:
      return 'idle';
  }
}

export function dotTextClass(tone: DotTone) {
  return TONE_TEXT[tone];
}

/** Small round status dot (green done, coral running, amber approval, red failed, grey idle). */
export function StatusDot({
  tone,
  status,
  label,
  className,
}: {
  tone?: DotTone;
  status?: string | null;
  label?: string;
  className?: string;
}) {
  const t = tone ?? toneForStatus(status);
  return (
    <span
      role="img"
      aria-label={label ?? status ?? t}
      className={cn('inline-block h-[7px] w-[7px] shrink-0 rounded-full align-middle', TONE_BG[t], className)}
    />
  );
}

export function StatusChip({ status }: { status: string }) {
  const { t: tr } = useTranslation();
  const t = toneForStatus(status);
  // Run statuses are translated (e.g. "gepland"); anything else keeps its readable raw form.
  const label = tr(`runStatus.${status}`, { defaultValue: status.replace(/_/g, ' ') });
  return (
    <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
      <StatusDot tone={t} status={status} />
      <span className={cn(t === 'idle' ? 'text-muted' : t === 'done' ? 'text-fg-2' : TONE_TEXT[t])}>
        {label}
      </span>
    </span>
  );
}
